import { useMemo } from 'react';
import { Select } from '@mantine/core';
import type { ExamQuestionMatchPair, ExamQuestionOption } from '../../../services/sampleExamTypes';
import { Button } from '../../../components/common/Button';
import type { UiQuestionType } from './types';
import classes from '../css/QuestionOptions.module.css';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function toControlOptions(options: ExamQuestionOption[]) {
  return options.map((o) => ({
    value: String(o.optionId),
    label: String(o.optionText ?? ''),
  }));
}

function mulberry32(seed: number) {
  let t = seed >>> 0;
  return function (): number {
    t = (t + 0x6d2b79f5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleSeeded<T>(arr: T[], seed: number): T[] {
  const rng = mulberry32(seed);
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Shuffle that guarantees at least one element moves vs the input order (length >= 2). */
function shuffledDifferentByKey<T>(arr: T[], seed: number, keyOf: (x: T) => string): T[] {
  if (arr.length <= 1) return arr;
  const originalKey = arr.map(keyOf).join('||');
  for (let i = 0; i < 20; i++) {
    const sh = shuffleSeeded(arr, seed + i * 31);
    if (sh.map(keyOf).join('||') !== originalKey) return sh;
  }
  const swap = [...arr];
  [swap[0], swap[1]] = [swap[1], swap[0]];
  return swap;
}

interface Props {
  uiType: UiQuestionType;
  /** Seed for shuffling (use questionId) so order is stable per question. */
  questionId?: number;
  options: ExamQuestionOption[];
  value: any;
  onChange: (val: any) => void;
  matchPairs?: ExamQuestionMatchPair[];
}

interface MatchPairProps {
  questionId?: number;
  matchPairs: ExamQuestionMatchPair[];
  value: any;
  onChange: (val: any) => void;
}

function MatchPairQuestion({ questionId, matchPairs, value, onChange }: MatchPairProps) {
  const pairsObj: Record<string, string> =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, string>)
      : {};

  const shuffledLeftPairs = useMemo(() => {
    const seed = ((questionId ?? 1) * 2654435761) >>> 0;
    return shuffledDifferentByKey(matchPairs, seed, (p) => String(p.leftText ?? ''));
  }, [matchPairs, questionId]);

  const rightTexts = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const p of matchPairs) {
      const rt = String(p.rightText ?? '').trim();
      if (rt && !seen.has(rt)) {
        seen.add(rt);
        out.push(rt);
      }
    }
    return out;
  }, [matchPairs]);

  const handleSelect = (leftText: string, right: string | null) => {
    const next: Record<string, string> = { ...pairsObj };
    if (right == null || right === '') {
      delete next[leftText];
    } else {
      next[leftText] = right;
    }
    onChange(next);
  };

  return (
    <div className={classes.matchPairGrid}>
      {shuffledLeftPairs.map((pair) => {
        const leftText = String(pair.leftText ?? '').trim();
        const currentRight = pairsObj[leftText] ?? '';
        const options = rightTexts.map((rt) => ({ value: rt, label: rt }));

        return (
          <div key={pair.pairId} className={classes.matchPairRow}>
            <div className={classes.matchLeft}>{leftText}</div>
            <div className={classes.matchRight}>
              <Select
                data={options}
                value={currentRight || null}
                onChange={(v) => handleSelect(leftText, v)}
                placeholder="Select match"
                radius="md"
                size="md"
                clearable
                searchable={false}
                comboboxProps={{ withinPortal: true }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function QuestionOptions({
  uiType,
  questionId,
  options,
  value,
  onChange,
  matchPairs,
}: Props) {
  if (uiType === 'single') {
    const displayOpts = toControlOptions(options);
    return (
      <div className={classes.optionsGrid}>
        {displayOpts.map((opt, i) => (
          <Button
            key={opt.value}
            className={`${classes.optionBtn} ${value === opt.value ? classes.selected : ''}`}
            onClick={() => onChange(opt.value)}
          >
            <span className={classes.optionLetter}>{LETTERS[i]}</span>
            {opt.label}
          </Button>
        ))}
      </div>
    );
  }

  if (uiType === 'match_pair' && matchPairs?.length) {
    return (
      <MatchPairQuestion
        questionId={questionId}
        matchPairs={matchPairs}
        value={value}
        onChange={onChange}
      />
    );
  }

  return null;
}
